// ⚠️  DEPRECATION NOTICE ⚠️
// This file is DEPRECATED and will be removed in a future version.
//
// MIGRATION INSTRUCTIONS:
// 1. Replace imports from '@/lib/constants' with useSettings() hook from '@/contexts/SettingsContext'
// 2. Use the legacy property for backward compatibility: const { legacy } = useSettings();
// 3. Example: const { LEAD_STATUS_OPTIONS } = legacy;
//
// This file is generated from resources/js/lib/config.json
// DO NOT EDIT MANUALLY - Update the JSON file instead

import configData from './config.json';

// Type definitions
export interface LeadStatusOption {
    value: string;
    label: string;
    color: string;
    isTerminal: boolean;
}

export interface LeadPriorityOption {
    value: string;
    label: string;
    color: string;
    icon: string;
}

export interface CompanyPriorityOption {
    value: string;
    label: string;
    color: string;
    icon: string;
}

export interface ProjectStatusOption {
    value: string;
    label: string;
    color: string;
    isTerminal: boolean;
}

export interface ProjectTaskStatusOption {
    value: string;
    label: string;
    color: string;
    isTerminal: boolean;
}

export interface ProjectTaskTypeOption {
    value: string;
    label: string;
    color: string;
}

export interface ProjectCountryTypeOption {
    value: string;
    label: string;
    color: string;
}

export interface TaskTypeOption {
    value: string;
    label: string;
    color: string;
}

export interface CostLineTypeOption {
    value: string;
    label: string;
}

export interface ProjectCostLineTypeOption {
    value: string;
    label: string;
}

export interface SelectOption {
    value: string;
    label: string;
}

// Export raw data with proper typing
export const CONSTANTS = configData;

// Country options for dropdowns
export const COUNTRY_OPTIONS: SelectOption[] = configData.countries.map((country) => ({
    value: country,
    label: country,
}));

// Company category options for dropdowns
export const COMPANY_CATEGORY_OPTIONS: SelectOption[] = configData.companyCategories.map((category) => ({
    value: category,
    label: category,
}));

// Company type options for dropdowns
export const COMPANY_TYPE_OPTIONS: SelectOption[] = configData.companyTypes.map((type) => ({
    value: type,
    label: type,
}));

// Lead status options with full configuration
export const LEAD_STATUS_OPTIONS: LeadStatusOption[] = configData.leadStatuses;

// Lead priority options with full configuration
export const LEAD_PRIORITY_OPTIONS: LeadPriorityOption[] = configData.leadPriorities;

// Company priority options with full configuration
export const COMPANY_PRIORITY_OPTIONS: CompanyPriorityOption[] = configData.companyPriorities;

// Lead source options for dropdowns
export const LEAD_SOURCE_OPTIONS: SelectOption[] = configData.leadSources.map((source) => ({
    value: source,
    label: source.charAt(0).toUpperCase() + source.slice(1).replace('_', ' '),
}));

// Project status options with full configuration
export const PROJECT_STATUS_OPTIONS: ProjectStatusOption[] = configData.projectStatuses;

// Project task status options with full configuration
export const PROJECT_TASK_STATUS_OPTIONS: ProjectTaskStatusOption[] = configData.projectTaskStatuses;

// Project task type options with full configuration
export const PROJECT_TASK_TYPE_OPTIONS: ProjectTaskTypeOption[] = configData.projectTaskTypes;

// Project country type options with full configuration
export const PROJECT_COUNTRY_TYPE_OPTIONS: ProjectCountryTypeOption[] = configData.projectCountryTypes;

// Task type options with full configuration
export const TASK_TYPE_OPTIONS: TaskTypeOption[] = configData.taskTypes;

// Cost line type options with full configuration
export const COST_LINE_TYPE_OPTIONS: CostLineTypeOption[] = configData.costLineTypes.map((type) => ({
    value: type.toLowerCase().replace(/\s+/g, '_'),
    label: type,
}));

// Project cost line type options with full configuration
export const PROJECT_COST_LINE_TYPE_OPTIONS: ProjectCostLineTypeOption[] = configData.projectCostLineTypes.map((type) => ({
    value: type.toLowerCase().replace(/\s+/g, '_'),
    label: type,
}));

// Terminal status configuration
export const PROJECT_TERMINAL_STATUSES = PROJECT_STATUS_OPTIONS.filter((status) => status.isTerminal).map((status) => status.value);
export const PROJECT_TASK_TERMINAL_STATUSES = PROJECT_TASK_STATUS_OPTIONS.filter((status) => status.isTerminal).map((status) => status.value);

// Helper functions
export const getCountryConfig = (value: string) => COUNTRY_OPTIONS.find((option) => option.value === value);
export const getCompanyCategoryConfig = (value: string) => COMPANY_CATEGORY_OPTIONS.find((option) => option.value === value);
export const getCompanyTypeConfig = (value: string) => COMPANY_TYPE_OPTIONS.find((option) => option.value === value);
export const getLeadStatusConfig = (value: string) => LEAD_STATUS_OPTIONS.find((option) => option.value === value);
export const getLeadPriorityConfig = (value: string) => LEAD_PRIORITY_OPTIONS.find((option) => option.value === value);
export const getCompanyPriorityConfig = (value: string) => COMPANY_PRIORITY_OPTIONS.find((option) => option.value === value);
export const getLeadSourceConfig = (value: string) => LEAD_SOURCE_OPTIONS.find((option) => option.value === value);
export const getProjectStatusConfig = (value: string) => PROJECT_STATUS_OPTIONS.find((option) => option.value === value);
export const getProjectTaskStatusConfig = (value: string) => PROJECT_TASK_STATUS_OPTIONS.find((option) => option.value === value);
export const getProjectTaskTypeConfig = (value: string) => PROJECT_TASK_TYPE_OPTIONS.find((option) => option.value === value);
export const getProjectCountryTypeConfig = (value: string) => PROJECT_COUNTRY_TYPE_OPTIONS.find((option) => option.value === value);
export const getTaskTypeConfig = (value: string) => TASK_TYPE_OPTIONS.find((option) => option.value === value);
export const getCostLineTypeConfig = (value: string) => COST_LINE_TYPE_OPTIONS.find((option) => option.value === value);
export const getProjectCostLineTypeConfig = (value: string) => PROJECT_COST_LINE_TYPE_OPTIONS.find((option) => option.value === value);

// Terminal status helper functions
export const isProjectStatusTerminal = (value: ProjectStatusValue): boolean =>
    PROJECT_TERMINAL_STATUSES.includes(value as (typeof PROJECT_TERMINAL_STATUSES)[number]);

export const isProjectTaskStatusTerminal = (value: ProjectTaskStatusValue): boolean =>
    PROJECT_TASK_TERMINAL_STATUSES.includes(value as (typeof PROJECT_TASK_TERMINAL_STATUSES)[number]);

// Type exports for TypeScript consumers
export type CountryValue = (typeof configData.countries)[number];
export type CompanyCategoryValue = (typeof configData.companyCategories)[number];
export type CompanyTypeValue = (typeof configData.companyTypes)[number];
export type CompanyPriorityValue = (typeof configData.companyPriorities)[number]['value'];
export type LeadStatusValue = (typeof configData.leadStatuses)[number]['value'];
export type LeadPriorityValue = (typeof configData.leadPriorities)[number]['value'];
export type LeadSourceValue = (typeof configData.leadSources)[number];
export type ProjectStatusValue = (typeof configData.projectStatuses)[number]['value'];
export type ProjectTaskStatusValue = (typeof configData.projectTaskStatuses)[number]['value'];
export type ProjectTaskTypeValue = (typeof configData.projectTaskTypes)[number]['value'];
export type ProjectCountryTypeValue = (typeof configData.projectCountryTypes)[number]['value'];
export type TaskTypeValue = (typeof configData.taskTypes)[number]['value'];
export type CostLineTypeValue = (typeof configData.costLineTypes)[number];
export type ProjectCostLineTypeValue = (typeof configData.projectCostLineTypes)[number];

// Work Order Types
export interface WorkOrderStatusOption {
    value: string;
    label: string;
    color: string;
}

export interface WorkOrderPriorityOption {
    value: string;
    label: string;
    color: string;
    icon: string;
}

export interface WorkOrderCostLineTypeOption {
    value: string;
    label: string;
}

export interface WorkOrderTypeOption {
    value: string;
    label: string;
    color: string;
}

export type WorkOrderStatusValue = (typeof configData.workOrderStatuses)[number]['value'] | 'proposal_sent' | 'in_progress_continued';
export type WorkOrderPriorityValue = (typeof configData.workOrderPriorities)[number]['value'];
export type WorkOrderCostLineTypeValue = (typeof configData.workOrderCostLineTypes)[number];
export type WorkOrderTypeValue = (typeof configData.workOrderTypes)[number]['value'];

// Work Order Constants
export const WORK_ORDER_STATUS_OPTIONS: WorkOrderStatusOption[] = configData.workOrderStatuses;
export const WORK_ORDER_PRIORITY_OPTIONS: WorkOrderPriorityOption[] = configData.workOrderPriorities;
export const WORK_ORDER_COST_LINE_TYPE_OPTIONS: WorkOrderCostLineTypeOption[] = configData.workOrderCostLineTypes.map((type) => ({
    value: type.toLowerCase().replace(/\s+/g, '_'),
    label: type,
}));
export const WORK_ORDER_TYPE_OPTIONS: WorkOrderTypeOption[] = configData.workOrderTypes;

// Work Order Helpers
export const getWorkOrderStatusConfig = (value: string) => WORK_ORDER_STATUS_OPTIONS.find((option) => option.value === value);
export const getWorkOrderPriorityConfig = (value: string) => WORK_ORDER_PRIORITY_OPTIONS.find((option) => option.value === value);
export const getWorkOrderCostLineTypeConfig = (value: string) => WORK_ORDER_COST_LINE_TYPE_OPTIONS.find((option) => option.value === value);
export const getWorkOrderTypeConfig = (value: string) => WORK_ORDER_TYPE_OPTIONS.find((option) => option.value === value);
